import React, { PropsWithChildren, useMemo, useEffect, useRef } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';

// Regex for matching "Acme Inc" and its variants as a complete company name, not individual words
const ACME_REPLACE_REGEX = /\b(acme\s*,?\s*inc\.?|acme\s*,?\s*inc|acme\s+inc\.?|acme inc)\b/gi;
const ACME_TEST_REGEX = /\b(acme\s*,?\s*inc\.?|acme\s*,?\s*inc|acme\s+inc\.?|acme inc)\b/i;
const ISO27001_REGEX = /\b(iso\s*-?\s*27001[:\s-]?\s*2013)\b/gi;
const ISMS_REGEX = /\b(isms)\b/gi;

// Fix for DED-1107
const COMPANY_NAME_MUSTACHE = /\{\{\s*Company\s+Name\s*\}\}/gi;
const ESCAPED_QUOTE_REGEX = /\\\"/g;

function normalizeText(input: string, companyName: string, skipIsmsReplacement: boolean = false) {
  let out = input;
  // Company name replacement (Acme variants)
  out = out.replace(ACME_REPLACE_REGEX, companyName);
  // Normalize ISO string
  out = out.replace(ISO27001_REGEX, 'ISO 27001');

  // Case-preserving ISMS -> MMS (only if not forced to override)
  if (!skipIsmsReplacement) {
    out = out.replace(ISMS_REGEX, (match) => (match === match.toUpperCase() ? 'MMS' : 'mms'));
  }

  out = out.replace(COMPANY_NAME_MUSTACHE, companyName);
  out = out.replace(ESCAPED_QUOTE_REGEX, "\"");
  return out;
}

// Helper to check if a node or its ancestors have the force-isms-override attribute
function shouldSkipIsmsReplacement(node: Node): boolean {
  let current: Node | null = node;
  while (current) {
    if (current instanceof Element && current.hasAttribute('data-force-isms-override')) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
}

function replaceTextInNode(node: Node, companyName: string) {
  if (node.nodeType === Node.TEXT_NODE && node.textContent) {
    const skipIsms = shouldSkipIsmsReplacement(node);
    const replaced = normalizeText(node.textContent, companyName, skipIsms);
    if (replaced !== node.textContent) {
      node.textContent = replaced;
    }
  }
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = Object.getPrototypeOf(el);
  const desc = Object.getOwnPropertyDescriptor(proto, 'value');
  if (desc && typeof desc.set === 'function') {
    desc.set.call(el, value);
  } else {
    el.value = value;
  }
  // Notify React-controlled inputs
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function replaceInElement(el: Element, companyName: string) {
  // Skip replacement if the element is currently focused (user is typing)
  if (el === document.activeElement) {
    return;
  }

  // Check if this element or its ancestors have the force-isms-override attribute
  const skipIsms = shouldSkipIsmsReplacement(el);

  // Replace attributes commonly used for visible text
  const attrNames = ['placeholder', 'title', 'aria-label'];
  for (const attr of attrNames) {
    const val = el.getAttribute(attr);
    if (val) {
      const replaced = normalizeText(val, companyName, skipIsms);
      if (replaced !== val) el.setAttribute(attr, replaced);
    }
  }

  // Replace control values/content (but skip if focused)
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') {
    const input = el as HTMLInputElement | HTMLTextAreaElement;
    if (typeof input.value === 'string' && input !== document.activeElement) {
      const replaced = normalizeText(input.value, companyName, skipIsms);
      if (replaced !== input.value) setNativeValue(input, replaced);
    }
  } else if (tag === 'SELECT') {
    const select = el as HTMLSelectElement;
    // Update each option's label/text
    for (const option of Array.from(select.options)) {
      if (option.text) {
        const replaced = normalizeText(option.text, companyName, skipIsms);
        if (replaced !== option.text) option.text = replaced;
      }
      if (option.label) {
        const replaced = normalizeText(option.label, companyName, skipIsms);
        if (replaced !== option.label) option.label = replaced;
      }
      if (option.value) {
        // Generally do not change option values, but if value is user-visible and contains ACME, normalize it
        const replaced = normalizeText(option.value, companyName, skipIsms);
        if (replaced !== option.value) option.value = replaced;
      }
    }
  } else if (tag === 'OPTION') {
    const option = el as HTMLOptionElement;
    if (option.text) {
      const replaced = normalizeText(option.text, companyName, skipIsms);
      if (replaced !== option.text) option.text = replaced;
    }
    if (option.label) {
      const replaced = normalizeText(option.label, companyName, skipIsms);
      if (replaced !== option.label) option.label = replaced;
    }
  }
}

function walkAndReplace(node: Node, companyName: string) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    replaceInElement(node as Element, companyName);
  }
  replaceTextInNode(node, companyName);
  node.childNodes.forEach(child => walkAndReplace(child, companyName));
}

type AcmeIncReplaceProviderProps = React.PropsWithChildren<{}>;

export const AcmeIncReplaceProvider: React.FC<AcmeIncReplaceProviderProps> = ({ children }) => {
  const { activeTenancy } = useTenancy();
  const companyName = activeTenancy?.name || activeTenancy?.legalName || 'Acme Inc';
  const rootRef = useRef<HTMLDivElement>(null);

  // Always call useEffect hook to maintain consistent hook order
  useEffect(() => {
    if (!rootRef.current) return;

    // Skip replacement if company name includes 'acme' or 'acme inc'
    if (ACME_TEST_REGEX.test(companyName)) {
      return;
    }

    // Initial replacement
    walkAndReplace(rootRef.current, companyName);
    if (typeof document !== 'undefined' && document.body) {
      walkAndReplace(document.body, companyName);
    }

    // Observe for dynamic changes
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => walkAndReplace(node, companyName));
        } else if (mutation.type === 'characterData' && mutation.target) {
          // Skip if the target element or its parent is focused (user is typing)
          const targetElement = mutation.target.parentElement || mutation.target;
          if (targetElement === document.activeElement) {
            return;
          }
          replaceTextInNode(mutation.target, companyName);
        } else if (mutation.type === 'attributes' && mutation.target instanceof Element) {
          replaceInElement(mutation.target, companyName);
        }
      });
    });
    const bodyObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => walkAndReplace(node, companyName));
        } else if (mutation.type === 'characterData' && mutation.target) {
          // Skip if the target element or its parent is focused (user is typing)
          const targetElement = mutation.target.parentElement || mutation.target;
          if (targetElement === document.activeElement) {
            return;
          }
          replaceTextInNode(mutation.target, companyName);
        } else if (mutation.type === 'attributes' && mutation.target instanceof Element) {
          replaceInElement(mutation.target, companyName);
        }
      });
    });
    observer.observe(rootRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label', 'value', 'label']
    });
    if (typeof document !== 'undefined' && document.body) {
      bodyObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['placeholder', 'title', 'aria-label', 'value', 'label']
      });
    }

    // Delegate input/change events to catch user edits and programmatic updates
    const handler = (e: Event) => {
      const target = e.target as Element | null;
      if (!target) return;
      // Skip replacement if the target is currently focused (user is actively typing)
      if (target === document.activeElement) {
        return;
      }
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || target instanceof HTMLOptionElement) {
        replaceInElement(target, companyName);
      }
    };
    const root = rootRef.current;
    root.addEventListener('input', handler, true);
    root.addEventListener('change', handler, true);
    const doc = typeof document !== 'undefined' ? document : undefined;
    if (doc) {
      doc.addEventListener('input', handler, true);
      doc.addEventListener('change', handler, true);
    }
    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
      root.removeEventListener('input', handler, true);
      root.removeEventListener('change', handler, true);
      if (doc) {
        doc.removeEventListener('input', handler, true);
        doc.removeEventListener('change', handler, true);
      }
    };
  }, [companyName]);

  // Handle conditional rendering without early returns
  // Edge case: if the company name includes 'acme' or 'acme inc', do not replace
  if (ACME_TEST_REGEX.test(companyName)) {
    return <>{children}</>;
  }

  return <div ref={rootRef}>{children}</div>;
};
